import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, CalendarDays, Clock, Target, GraduationCap, Building2, TrendingUp, CheckCircle2, X, Plus, User } from "lucide-react";
import { toast } from "sonner";

export default function HRHub() {
  return (
    <div className="space-y-6" data-testid="hr-hub-page">
      <div>
        <h1 className="text-3xl font-bold font-display">HR &amp; People</h1>
        <p className="text-sm text-neutral-500 mt-1">Workforce insights, leave, timesheets, reviews, and learning.</p>
      </div>
      <Tabs defaultValue="workforce">
        <TabsList className="bg-neutral-100 flex-wrap h-auto">
          <TabsTrigger value="workforce" data-testid="hr-tab-workforce"><TrendingUp className="w-4 h-4 mr-1" /> Workforce</TabsTrigger>
          <TabsTrigger value="orgchart" data-testid="hr-tab-orgchart"><Building2 className="w-4 h-4 mr-1" /> Org chart</TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-tab-leave"><CalendarDays className="w-4 h-4 mr-1" /> Leave</TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="hr-tab-timesheets"><Clock className="w-4 h-4 mr-1" /> Timesheets</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="hr-tab-reviews"><Target className="w-4 h-4 mr-1" /> Reviews</TabsTrigger>
          <TabsTrigger value="learning" data-testid="hr-tab-learning"><GraduationCap className="w-4 h-4 mr-1" /> Learning</TabsTrigger>
        </TabsList>
        <TabsContent value="workforce" className="mt-6"><WorkforceTab /></TabsContent>
        <TabsContent value="orgchart" className="mt-6"><OrgChartTab /></TabsContent>
        <TabsContent value="leave" className="mt-6"><LeaveTab /></TabsContent>
        <TabsContent value="timesheets" className="mt-6"><TimesheetsTab /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ReviewsTab /></TabsContent>
        <TabsContent value="learning" className="mt-6"><LearningTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500"><Icon className="w-3.5 h-3.5 text-pink" /> {label}</div>
        <div className="text-3xl font-bold font-display mt-2">{value}</div>
        {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function WorkforceTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/hr/workforce-dashboard").then(r=>setData(r.data)).catch(()=>setData(null)); }, []);
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="workforce-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Headcount" value={data.headcount} testId="wf-headcount" />
        <StatCard icon={CheckCircle2} label="New hires (30d)" value={data.new_hires_30d} testId="wf-hires-30d" />
        <StatCard icon={CalendarDays} label="Pending leave" value={data.pending_leave} sub="waiting on approval" testId="wf-pending-leave" />
        <StatCard icon={Clock} label="Pending timesheets" value={data.pending_timesheets} testId="wf-pending-ts" />
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">Headcount by department</h3>
          <div className="space-y-3">
            {data.by_department.map(d => (
              <div key={d.name} data-testid={`wf-dept-${d.name}`}>
                <div className="flex justify-between text-sm mb-1"><span>{d.name}</span><span className="font-mono text-neutral-500">{d.count}</span></div>
                <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-pink" style={{ width: `${data.headcount ? (d.count/data.headcount*100) : 0}%` }} /></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function OrgChartTab() {
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/hr/org-chart").then(r=>setOrg(r.data)).catch(()=>setOrg(null)); }, []);
  if (!org) return <div className="text-sm text-neutral-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="org-chart">
      <p className="text-sm text-neutral-500">{org.total} employees across {org.departments.length} departments</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {org.departments.filter(d => d.members?.length > 0).map(d => (
          <Card key={d.name} className="border-0 shadow-sm" data-testid={`org-dept-${d.name}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{d.name}</h3>
                <Badge variant="outline" className="text-[10px]">{d.members.length}</Badge>
              </div>
              <div className="space-y-2">
                {d.members.slice(0, 8).map(m => (
                  <div key={m.employee_id} className="flex items-center gap-2 text-sm" data-testid={`org-emp-${m.employee_id}`}>
                    <div className="w-8 h-8 rounded-full bg-pink/10 grid place-items-center text-pink text-xs font-bold">{(m.name||"?").split(" ").map(x=>x[0]).slice(0,2).join("")}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{m.name}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{m.title || m.role}</div>
                    </div>
                  </div>
                ))}
                {d.members.length > 8 && <div className="text-[11px] text-neutral-400">+{d.members.length - 8} more</div>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function LeaveTab() {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/hr/leave/all").then(r=>setReqs(r.data)).catch(()=>setReqs([]));
  useEffect(() => { load(); }, []);
  const decide = async (req_id, decision) => {
    await api.patch(`/hr/leave/${req_id}/decide`, { decision });
    toast.success(`Leave ${decision.toLowerCase()}`); load();
  };
  return (
    <div className="space-y-3" data-testid="leave-admin">
      {reqs.length === 0 && <div className="text-sm text-neutral-500">No leave requests yet.</div>}
      {reqs.map(r => (
        <Card key={r.req_id} className="border-0 shadow-sm" data-testid={`leave-${r.req_id}`}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm">{r.employee_name} · <Badge variant="outline" className="text-[9px]">{r.type}</Badge></div>
              <div className="text-xs text-neutral-500 mt-1">{r.start_date} → {r.end_date} · {r.days} day(s)</div>
              {r.reason && <div className="text-xs text-neutral-600 mt-1 italic">"{r.reason}"</div>}
            </div>
            <div className="flex items-center gap-2">
              {r.status === "PENDING" ? (
                <>
                  <Button size="sm" onClick={()=>decide(r.req_id,"APPROVED")} className="bg-teal hover:bg-teal/90 text-white" data-testid={`leave-approve-${r.req_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={()=>decide(r.req_id,"REJECTED")} className="text-red-500 border-red-200" data-testid={`leave-reject-${r.req_id}`}><X className="w-3.5 h-3.5" /></Button>
                </>
              ) : (
                <Badge variant="outline" className={r.status==="APPROVED"?"bg-teal/10 text-teal border-teal/20":"bg-red-50 text-red-500 border-red-200"}>{r.status}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimesheetsTab() {
  const [ts, setTs] = useState([]);
  const load = () => api.get("/hr/timesheets/pending").then(r=>setTs(r.data)).catch(()=>setTs([]));
  useEffect(() => { load(); }, []);
  const approve = async (ts_id) => {
    await api.patch(`/hr/timesheets/${ts_id}/approve`);
    toast.success("Approved"); load();
  };
  return (
    <div className="space-y-3" data-testid="timesheets-admin">
      {ts.length === 0 && <div className="text-sm text-neutral-500">No timesheets waiting for approval.</div>}
      {ts.map(t => (
        <Card key={t.ts_id} className="border-0 shadow-sm" data-testid={`ts-${t.ts_id}`}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm">{t.employee_name}</div>
              <div className="text-xs text-neutral-500 mt-1">Week of {t.week_start} · <span className="font-mono">{t.total_hours}h</span> total</div>
            </div>
            <Button size="sm" onClick={()=>approve(t.ts_id)} className="btn-primary" data-testid={`ts-approve-${t.ts_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/hr/reviews/team").then(r=>setReviews(r.data)).catch(()=>setReviews([])); }, []);
  return (
    <div className="space-y-3" data-testid="reviews-admin">
      {reviews.length === 0 && <div className="text-sm text-neutral-500">No reviews submitted yet. Employees start with self-assessments each cycle.</div>}
      {reviews.map(r => (
        <Card key={r.review_id} className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-semibold text-sm">{r.employee_name}</div>
              <div className="text-xs text-neutral-500 mt-1">Cycle {r.cycle} · <Badge variant="outline" className="text-[9px]">{r.status}</Badge></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LearningTab() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/hr/courses").then(r=>setCourses(r.data)).catch(()=>setCourses([])); }, []);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learning-catalog">
      {courses.map(c => (
        <Card key={c.course_id} className="border-0 shadow-sm" data-testid={`course-${c.course_id}`}>
          <CardContent className="p-5">
            <Badge variant="outline" className="text-[9px] mb-2">{c.category}</Badge>
            <h3 className="font-semibold">{c.title}</h3>
            <p className="text-xs text-neutral-500 mt-1">{c.description}</p>
            <div className="text-xs text-neutral-500 mt-3">{c.duration_min} min · {c.provider}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
